import { CommonModule } from "@angular/common";
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnDestroy, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { IonContent, IonicModule } from "@ionic/angular";
import * as models from "@models/index";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { SPORTS_PATHS } from "@shared-constants/routes";
import { CodereFooterComponent } from "@shared-mso-components/footer/codere-footer/codere-footer";
import { SbSimpleNavbarComponent } from "@sports-components/index";
import { SbTabsComponent } from "@sports-components/index";
import * as sportsModels from "@sports-models/index";
import { SportsBasePage, TrackingInfo } from "@sports-pages/index";
import { SportsExtractHourPipe } from "@sports-pipes/index";
import { SportNavigationService } from "@sports-services/index";
import { SportsbookState } from "@sports-stores/sportsbook/index";
import { SportsbookFacade } from "@sports-stores/sportsbook/sportsbook.facade";
import * as sportsbookSelector from "@sports-stores/sportsbook/sportsbook.selectors";
import { BehaviorSubject, Observable, Subscription, of } from "rxjs";
import { switchMap } from "rxjs/operators";

@Component({
  selector: "page-race",
  templateUrl: "./race.page.html",
  styleUrls: ["./race.page.scss"],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    SbSimpleNavbarComponent,
    CodereFooterComponent,
    TranslateModule,
    SportsExtractHourPipe,
    SbTabsComponent,
  ],
})
export class HorsesPage extends SportsBasePage implements OnDestroy {
  @ViewChild(IonContent) content: IonContent;
  race: any;

  store = inject(Store<SportsbookState>);
  sportsbookFacade = inject(SportsbookFacade);
  filters;
  currentFilter$: BehaviorSubject<any> = new BehaviorSubject(null);
  endOfToday;

  globals: models.GlobalVarsModel;

  router = inject(Router);
  newBaseService = inject(NewBaseService);
  deviceService = inject(DeviceService);
  sportNavigationService = inject(SportNavigationService);
  isDesktop: boolean = false;
  isMobile: boolean = false;

  currentId: number;

  raceData$: Observable<any>;
  raceDataSub$: Subscription;
  nextRaces: sportsModels.C_Horses[] = [];
  todayRaces: sportsModels.C_Horses[] = [];
  tomorrowRaces: sportsModels.C_Horses[] = [];

  selectedTodayRace = 0;
  selectedTomorrowRace = 0;
  showedItems = {
    highlights: false,
    today: false,
    tomorrow: false,
  };

  sportsNavigationId = undefined;
  firstIterationRestore = false;
  stateToSave = {
    selectedTodayRace: undefined,
    selectedTomorrowRace: undefined,
    currentFilter: undefined,
    scrollPosition: undefined,
  };
  stateToRestore = undefined;

  constructor() {
    super();
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
    this.sportsNavigationId = currentUrl.state.sportsNavigationId;
    this.race = currentUrl.state.params;
    this.initializePage();

    this.raceData$ = this.store.select(sportsbookSelector.getRaceDataState);
    this.raceDataSub$ = this.raceData$.subscribe((raceData) => {
      this.filters = raceData?.filters;
      this.currentFilter$.next(raceData?.selectedFilter);

      this.nextRaces = raceData?.next;
      this.todayRaces = raceData?.today;
      this.tomorrowRaces = raceData?.tomorrow;
      this.showedItems = {
        highlights: this.nextRaces?.length > 0,
        today: this.todayRaces?.length > 0,
        tomorrow: this.tomorrowRaces?.length > 0,
      };

      const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
      if (currentUrl?.popstate && !this.firstIterationRestore) {
        this.sportsNavigationId = currentUrl.state.sportsNavigationId;
        this.stateToRestore = this.sportNavigationService.getRestoreStateFromHistory(this.sportsNavigationId);
        if (this.stateToRestore) {
          this.stateToSave = this.stateToRestore;
        }
        this.selectedTodayRace = this.stateToRestore?.selectedTodayRace || 0;
        this.selectedTomorrowRace = this.stateToRestore?.selectedTomorrowRace || 0;
        this.currentFilter$.next(this.stateToRestore?.currentFilter);
        setTimeout(() => {
          this.content.scrollToPoint(undefined, this.stateToRestore?.scrollPosition || 0, 250);
        }, 150);
        this.firstIterationRestore = true;
      }
    });
  }

  initializePage() {
    this.loadContent();
    this.newBaseService.getVars
      .pipe(
        switchMap((data: models.GlobalVarsModel) => {
          this.globals = data;
          return of(null);
        }),
      )
      .subscribe();
  }

  private loadContent() {
    const UTCHours = 23 + new Date().getTimezoneOffset() / 60;
    this.endOfToday = new Date(new Date().setUTCHours(UTCHours, 59, 59, 999));

    if (Object.keys(this.race).length < 1) {
      this.race.SportHandle = sportsModels.C_Horses.races.type.horse;
    }
    this.sportsbookFacade.fetchRace(this.race.SportHandle, this.endOfToday);
  }

  selectedTodayRaceChange(selection) {
    if (selection?.detail?.value) this.selectedTodayRace = +selection?.detail?.value;
    if (this.isDesktop) this.selectedTomorrowRace = 0;
    this.updateLastStateToRestore(this.selectedTodayRace, this.selectedTomorrowRace, undefined, undefined);
  }

  selectedTomorrowRaceChange(selection) {
    if (selection?.detail?.value) this.selectedTomorrowRace = +selection?.detail?.value;
    if (this.isDesktop) this.selectedTodayRace = 0;
    this.updateLastStateToRestore(this.selectedTodayRace, this.selectedTomorrowRace, undefined, undefined);
  }

  setCurrentFilter(event) {
    this.currentFilter$?.next(event);
    this.updateLastStateToRestore(undefined, undefined, event, undefined);
  }

  getTrackingInfo(): TrackingInfo {
    return {
      uri: encodeURI(`/ ${this.race.SportHandle} `),
      description: `Horses page`,
      additionalData: {
        nodeId: this.race.NodeId,
        nodeName: this.race.SportHandle,
      },
    };
  }

  navigate(race: sportsModels.C_Horses, countryCode?: string) {
    this.updateLastStateToRestore(undefined, undefined, this.currentFilter$.getValue(), undefined);
    const raceParams = {
      ...race,
      CountryCode: countryCode,
    };
    this.router.navigate([SPORTS_PATHS.HorsesMarketPage], {
      state: { params: raceParams },
    });
  }

  onScroll(ev) {
    this.updateLastStateToRestore(undefined, undefined, undefined, ev.detail.scrollTop);
  }

  ngOnDestroy() {
    this.raceDataSub$.unsubscribe();
    this.sportNavigationService.injectLastStateToHistory(this.sportsNavigationId, this.stateToSave);
  }

  updateLastStateToRestore(selectedTodayRace, selectedTomorrowRace, currentFilter, scrollPosition) {
    const newStateToSave = { ...this.stateToSave };
    if (selectedTodayRace !== undefined) {
      newStateToSave.selectedTodayRace = selectedTodayRace;
    }
    if (selectedTomorrowRace !== undefined) {
      newStateToSave.selectedTomorrowRace = selectedTomorrowRace;
    }
    if (currentFilter !== undefined) {
      newStateToSave.currentFilter = currentFilter;
    }
    if (scrollPosition !== undefined) {
      newStateToSave.scrollPosition = scrollPosition;
    }
    this.stateToSave = newStateToSave;
  }
}
